<template>
  <div class="w-100 portfolio-tiles-wrap">
    <div class="row g-3">
      <div
        v-for="share in positionRows"
        :key="share.ISINID || share.ISIN || shareName(share)"
        class="col-12 col-xl-6"
      >
        <div
          class="border-0 shadow-sm p-4 h-100 rounded-4 d-flex flex-column justify-content-between option-card overflow-hidden is-clickable"
          :class="isDarkMode ? 'option-card-dark' : 'bg-white option-card-light'"
          :style="{
            '--stock-color': share.Clr || '#64748b',
            cursor: 'pointer',
            backgroundColor: isDarkMode ? '#1E293B' : '#ffffff',
            border: isDarkMode ? '1px solid #334155' : '1px solid transparent',
            borderRadius: '0.65rem'
          }"
          @click="onCardClick(share)"
        >
          <div class="d-flex justify-content-between align-items-start mb-3 w-100">
            <div class="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
              <div
                class="d-flex align-items-center justify-content-center fw-bold share-logo-wrap"
                :class="{ 'share-logo-fallback': !showShareLogo(share) }"
                :style="showShareLogo(share)
                  ? { width: '40px', height: '40px', backgroundColor: 'transparent', flexShrink: 0 }
                  : {
                      width: '40px',
                      height: '40px',
                      backgroundColor: hexToRgbA(share.Clr || '#64748b', isDarkMode ? 0.22 : 0.12),
                      color: share.Clr || '#64748b',
                      fontSize: '0.85rem',
                      flexShrink: 0,
                      borderRadius: '50%'
                    }"
              >
                <img
                  v-if="showShareLogo(share)"
                  :src="shareLogoUrl(share.ISIN)"
                  :alt="shareName(share)"
                  class="share-logo-img"
                  @error="onLogoError(share.ISIN)"
                />
                <span v-else>{{ shareMonogram(shareName(share)) }}</span>
              </div>

              <div class="min-w-0">
                <h6
                  class="fw-bold text-truncate mb-0"
                  :style="{ maxWidth: '100%', color: isDarkMode ? '#E2E8F0' : '#1E293B', fontSize: '1rem' }"
                >{{ shareName(share) }}</h6>
                <span
                  v-if="share.ISIN"
                  class="small font-monospace d-block text-truncate"
                  :class="isDarkMode ? 'text-slate-400' : 'text-muted'"
                  style="font-size: 0.75rem;"
                >{{ share.ISIN }}</span>
              </div>
            </div>

            <div class="d-flex align-items-center justify-content-end ps-2 align-self-center gap-2" style="flex-shrink: 0;">
              <button
                type="button"
                class="forum-bubble-btn"
                :class="{ 'forum-bubble-btn-dark': isDarkMode }"
                :title="'Fórum: ' + shareName(share)"
                @click.stop="openShareForum(share)"
              >
                <fa-icon icon="fa-regular fa-comment-dots" />
              </button>
              <div class="d-flex align-items-center justify-content-end" style="width: 70px; height: 24px; flex-shrink: 0;">
                <template v-if="trendType(share) === 1">
                  <svg v-if="getMovementSize(share.PctDf) === 'big'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                    <path d="M2 18C12 18 15 12 22 8C29 4 36 2 48 2" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="getMovementSize(share.PctDf) === 'medium'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                    <path d="M2 16C10 15 15 10 23 9C31 8 38 5 48 3" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="60" height="20" viewBox="0 0 50 20" fill="none">
                    <path d="M2 12 C12 12 24 11 36 10 L48 9" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </template>
                <template v-else-if="trendType(share) === -1">
                  <svg v-if="getMovementSize(share.PctDf) === 'big'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                    <path d="M2 2 C12 2 15 8 22 12C29 16 36 18 48 18" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="getMovementSize(share.PctDf) === 'medium'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                    <path d="M2 4 C10 5 15 10 23 11C31 12 38 15 48 17" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="60" height="20" viewBox="0 0 50 20" fill="none">
                    <path d="M2 8 C12 8 24 9 36 10 L48 11" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </template>
                <svg v-else width="60" height="20" viewBox="0 0 50 20" fill="none">
                  <path d="M2 10 C10 12 18 6 25 10C32 14 40 8 48 10" :stroke="isDarkMode ? '#64748B' : '#CBD5E1'" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-end mb-2">
            <div class="d-flex align-items-baseline gap-2">
              <h3
                class="fw-extrabold mb-0"
                :class="isDarkMode ? 'text-white' : 'text-dark'"
                style="letter-spacing: -0.5px;"
              >{{ formatFn(share.Prc ?? share.ISINPrice) }} <span class="fs-6 fw-medium" :class="isDarkMode ? 'text-slate-400' : 'text-secondary'">Kč</span></h3>
              <i
                class="bi fs-5 lh-1"
                :class="trendType(share) === 1
                  ? 'bi-graph-up text-success'
                  : (trendType(share) === -1
                    ? 'bi-graph-down text-danger'
                    : (isDarkMode ? 'bi-dash-lg text-slate-400' : 'bi-dash-lg text-muted'))"
              ></i>
            </div>
            <div class="d-flex flex-column align-items-end" :class="isDarkMode ? 'text-slate-400' : 'text-muted'" style="font-size: 0.75rem;">
              <span class="mb-1">předchozí závěr ({{ formatFn(share.LPrc) }})</span>
              <span
                class="badge px-2.5 py-1 rounded-pill fw-semibold small"
                :class="trendType(share) === 1
                  ? (isDarkMode ? 'badge-change-up-dark' : 'bg-success-light text-success')
                  : (trendType(share) === -1
                    ? (isDarkMode ? 'badge-change-down-dark' : 'bg-danger-light text-danger')
                    : (isDarkMode ? 'badge-change-flat-dark' : 'bg-light text-secondary'))"
              >
                {{ trendType(share) === 1 ? '+' : '' }}{{ share.PctDf }}%
              </span>
            </div>
          </div>

          <div class="range-block mb-3">
            <div
              class="position-relative w-100 my-2"
              :style="{ height: '4px', backgroundColor: isDarkMode ? '#0F172A' : '#F1F5F9', borderRadius: '2px' }"
            >
              <div
                class="position-absolute rounded-circle shadow-sm"
                :style="{
                  left: calculatePosition(share.Prc ?? share.ISINPrice, share.Mn, share.Mx) + '%',
                  top: '-4px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'var(--stock-color)',
                  transform: 'translateX(-50%)',
                  transition: 'left 0.3s ease'
                }"
              ></div>
            </div>
            <div class="range-footer" :class="isDarkMode ? 'text-slate-400' : 'text-muted'">
              <div class="range-end">Min: <strong :class="dfClass(share.MnDfType)">{{ formatFn(share.Mn) }}</strong> <span class="ms-1" :class="dfClass(share.MnDfType)">({{ share.MnDf }})</span></div>
              <div class="range-title">90denní rozpětí</div>
              <div class="range-end range-end-max">Max: <strong :class="dfClass(share.MxDfType)">{{ formatFn(share.Mx) }}</strong> <span class="ms-1" :class="dfClass(share.MxDfType)">({{ share.MxDf }})</span></div>
            </div>
          </div>

          <div
            class="position-stats"
            :class="[
              isDarkMode ? 'position-stats-dark' : 'position-stats-light',
              smartLegs(share).length ? 'mb-1' : 'mb-0'
            ]"
          >
            <div class="stat-col stat-position">
              <span v-if="isLong(share)" class="side-badge is-long">LONG</span>
              <span v-else-if="isShort(share)" class="side-badge is-short">SHORT</span>
              <div class="stat-label">Pozice</div>
              <div class="stat-value stat-value-qty">
                {{ formatQty(share) }}<span class="stat-unit">ks</span>
              </div>
            </div>
            <div class="stat-col stat-actions">
              <div class="stat-label">Akce</div>
              <div class="stat-actions-row">
                <button
                  v-if="share.ISINID"
                  type="button"
                  class="trade-action-btn trade-action-close"
                  :class="{ 'trade-action-btn-dark': isDarkMode }"
                  title="Uzavřít pozici"
                  @click.stop="closePosition(share)"
                >
                  <fa-icon icon="fa-solid fa-file-export" />
                </button>
                <button
                  v-if="share.ISINID && (isLong(share) || isShort(share))"
                  type="button"
                  class="trade-action-btn trade-action-shield"
                  :class="{ 'trade-action-btn-dark': isDarkMode }"
                  title="Inteligentní pokyn"
                  @click.stop="manageOrders(share)"
                >
                  <fa-icon icon="fa-solid fa-shield-halved" />
                </button>
                <button
                  v-if="share.ISINID && (isLong(share) || isShort(share))"
                  type="button"
                  class="trade-action-btn"
                  :class="[
                    isShort(share) ? 'trade-action-short' : 'trade-action-long',
                    {
                      'trade-action-btn-dark': isDarkMode,
                      'is-disabled': cannotAfford(share)
                    }
                  ]"
                  :disabled="cannotAfford(share)"
                  :style="cannotAfford(share)
                    ? { color: '#94A3B8', opacity: '0.32', filter: 'grayscale(1)' }
                    : null"
                  :title="cannotAfford(share)
                    ? 'Nedostatek volného kapitálu na 1 kus'
                    : (isShort(share) ? 'Navýšit SHORT' : 'Navýšit LONG')"
                  @click.stop="increasePosition(share)"
                >
                  <fa-icon
                    :icon="isShort(share) ? 'fa-solid fa-circle-arrow-down' : 'fa-solid fa-circle-chevron-up'"
                    :style="cannotAfford(share) ? { color: '#94A3B8' } : null"
                  />
                </button>
              </div>
            </div>
            <div class="stat-col stat-col-open">
              <div class="stat-label">Ot. cena</div>
              <div class="stat-value">{{ formatFn(share.ISINAvgPrice) }}</div>
              <div class="stat-sub stat-sub-change" :class="priceChangeClass(share)">
                <span class="stat-sub-label">Změna</span>
                {{ formatPriceChange(share) }}
              </div>
            </div>
          </div>

          <div
            v-if="smartProtectLeg(share) || smartTpLeg(share)"
            class="smart-lines"
            :class="isDarkMode ? 'smart-lines-dark' : 'smart-lines-light'"
          >
            <div class="smart-col is-left">
              <div
                v-if="smartProtectLeg(share)"
                class="smart-line"
                :class="'is-' + smartProtectLeg(share).kind.toLowerCase()"
              >
                <span class="smart-line-badge" :title="smartProtectLeg(share).title">{{ smartProtectLeg(share).label }}</span>
                <span class="smart-line-limit">{{ formatFn(smartProtectLeg(share).price) }}<span v-if="smartProtectLeg(share).pct" class="smart-line-pct"> ({{ smartProtectLeg(share).pct }})</span></span>
                <span class="smart-line-actions">
                  <button
                    type="button"
                    class="smart-line-btn"
                    title="Upravit inteligentní pokyn"
                    @click.stop="manageOrders(share)"
                  >
                    <fa-icon icon="fa-solid fa-pencil" />
                  </button>
                  <button
                    type="button"
                    class="smart-line-btn is-clear"
                    title="Smazat inteligentní pokyn"
                    @click.stop="clearOrders(share, smartProtectLeg(share))"
                  >
                    <fa-icon icon="fa-solid fa-xmark" />
                  </button>
                </span>
              </div>
            </div>
            <div class="smart-col is-right">
              <div
                v-if="smartTpLeg(share)"
                class="smart-line"
                :class="'is-' + smartTpLeg(share).kind.toLowerCase()"
              >
                <span class="smart-line-badge" :title="smartTpLeg(share).title">{{ smartTpLeg(share).label }}</span>
                <span class="smart-line-limit">{{ formatFn(smartTpLeg(share).price) }}<span v-if="smartTpLeg(share).pct" class="smart-line-pct"> ({{ smartTpLeg(share).pct }})</span></span>
                <span class="smart-line-actions">
                  <button
                    type="button"
                    class="smart-line-btn"
                    title="Upravit inteligentní pokyn"
                    @click.stop="manageOrders(share)"
                  >
                    <fa-icon icon="fa-solid fa-pencil" />
                  </button>
                  <button
                    type="button"
                    class="smart-line-btn is-clear"
                    title="Smazat inteligentní pokyn"
                    @click.stop="clearOrders(share, smartTpLeg(share))"
                  >
                    <fa-icon icon="fa-solid fa-xmark" />
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="cashRow && cashAsTile" class="col-12 col-xl-6">
        <div
          class="border-0 shadow-sm p-4 h-100 rounded-4 d-flex flex-column justify-content-between option-card overflow-hidden"
          :class="isDarkMode ? 'option-card-dark' : 'bg-white option-card-light'"
          :style="{
            backgroundColor: isDarkMode ? '#1E293B' : '#ffffff',
            border: isDarkMode ? '1px solid #334155' : '1px solid transparent',
            borderRadius: '0.65rem'
          }"
        >
          <div class="d-flex align-items-center gap-3 min-w-0">
            <div class="d-flex align-items-center justify-content-center fw-bold cash-logo">CZK</div>
            <h6
              class="fw-bold text-truncate mb-0"
              :style="{ color: isDarkMode ? '#E2E8F0' : '#1E293B', fontSize: '1rem' }"
            >{{ shareName(cashRow) }}</h6>
          </div>

          <div class="cash-tile-amount-wrap">
            <h3
              class="fw-extrabold mb-0 cash-tile-amount"
              :class="isDarkMode ? 'text-white' : 'text-dark'"
              style="letter-spacing: -0.5px;"
            >{{ formatFn(cashDisplayAmount(cashRow)) }} <span class="cash-tile-currency" :class="isDarkMode ? 'text-slate-400' : 'text-secondary'">Kč</span></h3>
          </div>

          <div
            v-if="hasInterest(cashRow)"
            class="cash-interest-block"
            :class="isDarkMode ? 'cash-interest-panel-dark' : 'cash-interest-panel-light'"
          >
            <div class="cash-interest-title">Úročení nevyužitého virtuálního kapitálu</div>
            <div class="cash-bands">
              <div class="cash-band is-active">
                <div class="stat-label">Do {{ formatLimit(cashRow) }} Kč</div>
                <div class="cash-band-rate">{{ formatRate(cashRow.InterestRate) }} <span>% p.a.</span></div>
              </div>
              <div
                class="cash-band is-high"
                :class="{ 'is-warn': highBandApplies, 'is-muted': !highBandApplies }"
                :style="highBandStyle"
              >
                <div class="stat-label">Nad {{ formatLimit(cashRow) }} Kč</div>
                <div class="cash-band-rate">{{ formatRate(cashRow.InterestRate100) }} <span>% p.a.</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="cashRow && !cashAsTile"
      class="cash-row mt-3"
      :class="isDarkMode ? 'cash-row-dark' : 'cash-row-light'"
    >
      <div class="d-flex align-items-center justify-content-center fw-bold cash-logo cash-logo-sm">CZK</div>
      <div class="min-w-0 flex-grow-1">
        <div class="fw-bold text-truncate cash-row-title">{{ shareName(cashRow) }}</div>
      </div>
      <div v-if="hasInterest(cashRow)" class="cash-row-interest">
        (úročení
        <span class="cash-row-rate is-good">{{ formatRate(cashRow.InterestRate) }} % p.a.</span>
        do {{ formatLimit(cashRow) }}
        <span :class="highBandApplies ? 'is-warn' : 'is-muted'">
          resp.
          <span class="cash-row-rate">{{ formatRate(cashRow.InterestRate100) }} % p.a.</span>
          nad {{ formatLimit(cashRow) }}
        </span>)
      </div>
      <div class="fw-extrabold cash-row-amount">
        {{ formatFn(cashDisplayAmount(cashRow)) }}
        <span class="fs-6 fw-medium" :class="isDarkMode ? 'text-slate-400' : 'text-secondary'">Kč</span>
      </div>
    </div>

  </div>
</template>

<script setup>
import { reactive, computed } from "vue";
import { useStore } from "vuex";
import { shareLogoUrl, shareMonogram } from "@/helpers/shareLogos";

const props = defineProps({
  rows: Array,
  availableCapital: {
    type: Number,
    default: null
  }
});
const emit = defineEmits(['action', 'clear-orders']);

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const tileRows = computed(() => {
  const src = props.rows;
  if (!src) return [];
  if (Array.isArray(src)) return src.filter((row) => row && typeof row === "object");
  if (src.ISINName != null || src.Nm != null || src.ISINID != null || src.PortfolioValue != null) {
    return [src];
  }
  return Object.values(src).filter((row) => row && typeof row === "object" && !Array.isArray(row));
});
const isCash = (share) => {
  const name = String(share?.Nm || share?.ISINName || "").toLowerCase();
  if (name.includes("nevyužitý") || name.includes("nevyuzity")) return true;
  const id = share?.ISINID;
  const isin = share?.ISIN;
  const hasId = id != null && String(id).trim() !== "" && Number(id) !== 0;
  const hasIsin = isin != null && String(isin).trim() !== "" && String(isin) !== "0";
  return !hasId && !hasIsin;
};
const positionRows = computed(() => tileRows.value.filter((row) => !isCash(row)));
const cashRow = computed(() => tileRows.value.find((row) => isCash(row)) || null);
const cashAsTile = computed(() => {
  const count = positionRows.value.length;
  return !!(cashRow.value && count > 0 && count % 2 === 1);
});
const logoFailed = reactive({});

const shareName = (share) => share?.Nm || share?.ISINName || "";
const positionQty = (share) => {
  const raw = share?.ISINAmount ?? share?.Amt;
  if (raw == null || raw === "") return 0;
  const n = Number(String(raw).replace(/\s+/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const isLong = (share) => positionQty(share) > 0;
const isShort = (share) => positionQty(share) < 0;
const trendType = (share) => Number(share?.PrcDfType);

const closePosition = (share) => {
  if (!share?.ISINID) return;
  emit('action', { ISINID: share.ISINID, closePosition: true });
};

const sharePrice = (share) => {
  const n = Number(String(share?.Prc ?? share?.ISINPrice ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const freeCash = computed(() => {
  if (props.availableCapital != null && Number.isFinite(props.availableCapital)) {
    return props.availableCapital;
  }
  if (!cashRow.value) return null;
  return parseNum(cashRow.value.PortfolioValue ?? cashRow.value.PtfVl);
});

const cannotAfford = (share) => {
  if (freeCash.value == null) return false;
  const price = sharePrice(share);
  return price > 0 && price > freeCash.value;
};

const increasePosition = (share) => {
  if (!share?.ISINID || cannotAfford(share)) return;
  emit('action', { ISINID: share.ISINID, side: isShort(share) ? 'short' : 'long' });
};

const manageOrders = (share) => {
  if (!share?.ISINID) return;
  emit('action', {
    ISINID: share.ISINID,
    side: isShort(share) ? 'short' : 'long',
    manageOrders: true
  });
};

const clearOrders = (share, leg) => {
  if (!share?.ISINID || !leg?.types?.length) return;
  emit('clear-orders', { ISINID: share.ISINID, types: leg.types });
};

const formatRate = (value) => {
  const n = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("cs-CZ", { maximumFractionDigits: 2 });
};

const hasInterest = (row) => formatRate(row?.InterestRate) != null && formatRate(row?.InterestRate100) != null;

const interestLimit = (row) => {
  const n = Number(String(row?.InterestLimit ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 100000;
};

const cashAmount = (row) => {
  const n = parseNum(row?.PortfolioValue ?? row?.PtfVl);
  return n != null ? n : 0;
};

const cashDisplayAmount = (row) => {
  return parseNum(row?.PortfolioValue ?? row?.PtfVl);
};

const formatLimit = (row) => interestLimit(row).toLocaleString("cs-CZ", { maximumFractionDigits: 0 });

const highBandApplies = computed(() => {
  if (!cashRow.value) return false;
  return cashAmount(cashRow.value) > interestLimit(cashRow.value);
});

const highBandStyle = computed(() => {
  if (!highBandApplies.value) {
    if (isDarkMode.value) {
      return {
        background: "#020617",
        borderColor: "#1E293B",
        boxShadow: "none",
        opacity: "0.38",
        filter: "grayscale(1)"
      };
    }
    return {
      background: "#CBD5E1",
      borderColor: "#94A3B8",
      boxShadow: "none",
      opacity: "0.72",
      filter: "grayscale(1)"
    };
  }
  if (isDarkMode.value) {
    return {
      background: "rgba(255, 138, 76, 0.16)",
      borderColor: "#FF8A4C",
      boxShadow: "0 2px 8px rgba(255, 138, 76, 0.28)"
    };
  }
  return {
    background: "#FFF4EC",
    borderColor: "#FF8A4C",
    boxShadow: "0 2px 8px rgba(255, 138, 76, 0.22)"
  };
});

const hexToRgbA = (hex, alpha) => {
  if (!hex) return `rgba(100, 116, 139, ${alpha})`;
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length == 3) c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    c = "0x" + c.join("");
    return `rgba(${(c >> 16) & 255}, ${(c >> 8) & 255}, ${c & 255}, ${alpha})`;
  }
  return `rgba(100, 116, 139, ${alpha})`;
};

const onLogoError = (isin) => {
  if (isin) logoFailed[isin] = true;
};

const showShareLogo = (share) => !!(shareLogoUrl(share.ISIN) && !logoFailed[share.ISIN]);

const parseNum = (val) => {
  if (val == null || val === "") return null;
  const num = parseFloat(String(val).replace(/\s+/g, "").replace(",", "."));
  return Number.isFinite(num) ? num : null;
};

const formatSignedPct = (pct) => {
  if (pct == null || !Number.isFinite(pct)) return "";
  const abs = Math.abs(pct);
  const body = abs.toLocaleString("cs-CZ", { maximumFractionDigits: 1, minimumFractionDigits: abs >= 10 ? 0 : 1 });
  if (pct > 0) return `+${body} %`;
  if (pct < 0) return `−${body} %`;
  return `0 %`;
};

const distToLimit = (share, limit) => {
  const px = sharePrice(share);
  const n = parseNum(limit);
  if (!px || n == null) return "";
  return formatSignedPct(((n - px) / px) * 100);
};

const smartLegs = (share) => {
  const legs = [];
  const sl = parseNum(share?.PO_SL);
  const tr = parseNum(share?.PO_TR);
  const tp = parseNum(share?.PO_TP);
  if (sl != null) {
    legs.push({
      kind: "SL",
      label: "SL",
      title: "Stop-loss",
      price: sl,
      pct: distToLimit(share, sl),
      types: ["POT_SL"]
    });
  } else if (tr != null) {
    const types = [];
    if (parseNum(share?.PO_TRP) != null) types.push("POT_TRP");
    if (parseNum(share?.PO_TRC) != null) types.push("POT_TRC");
    if (!types.length) types.push("POT_TRP", "POT_TRC");
    legs.push({
      kind: "TR",
      label: "Trail",
      title: "Trailing",
      price: tr,
      pct: distToLimit(share, tr),
      types
    });
  }
  if (tp != null) {
    legs.push({
      kind: "TP",
      label: "TP",
      title: "Take-profit",
      price: tp,
      pct: distToLimit(share, tp),
      types: ["POT_TP"]
    });
  }
  return legs;
};

const smartProtectLeg = (share) => smartLegs(share).find((leg) => leg.kind === "SL" || leg.kind === "TR") || null;

const smartTpLeg = (share) => smartLegs(share).find((leg) => leg.kind === "TP") || null;

const formatFn = (val) => {
  const num = parseNum(val);
  if (num == null) return "—";
  const hasDecimals = num % 1 !== 0;
  return num.toLocaleString("cs-CZ", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  });
};

const formatQty = (share) => {
  const n = Math.abs(positionQty(share));
  return n.toLocaleString("cs-CZ", { maximumFractionDigits: 0 });
};

const priceChange = (share) => {
  const current = parseNum(share?.Prc ?? share?.ISINPrice);
  const open = parseNum(share?.ISINAvgPrice);
  if (current == null || open == null || open === 0) return null;
  return {
    abs: current - open,
    pct: ((current - open) / open) * 100
  };
};

const formatPriceChange = (share) => {
  const change = priceChange(share);
  if (!change) return "—";
  const sign = change.abs > 0 ? "+" : change.abs < 0 ? "−" : "";
  const absVal = formatFn(Math.abs(change.abs));
  const pct = Math.abs(change.pct).toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${sign}${absVal} (${sign}${pct} %)`;
};

const priceChangeClass = (share) => {
  const change = priceChange(share);
  if (!change || change.abs === 0) return "";
  const priceUp = change.abs > 0;
  const goodForPosition = isShort(share) ? !priceUp : priceUp;
  return goodForPosition ? "text-success" : "text-danger";
};

const dfClass = (type) => {
  const n = Number(type);
  if (n === 1) return "text-success";
  if (n === -1) return "text-danger";
  return isDarkMode.value ? "text-slate-200" : "text-dark";
};

const calculatePosition = (current, min, max) => {
  const curNum = parseFloat(String(current).replace(/\s+/g, "").replace(",", "."));
  const minNum = parseFloat(String(min).replace(/\s+/g, "").replace(",", "."));
  const maxNum = parseFloat(String(max).replace(/\s+/g, "").replace(",", "."));
  if (isNaN(curNum) || isNaN(minNum) || isNaN(maxNum) || maxNum === minNum) return 50;
  return Math.min(Math.max(((curNum - minNum) / (maxNum - minNum)) * 100, 0), 100);
};

const getMovementSize = (pctDf) => {
  const pct = Math.abs(parseFloat(String(pctDf ?? "").replace("+", "").replace(",", ".")));
  if (isNaN(pct)) return "small";
  if (pct >= 4.5) return "big";
  if (pct >= 1.0) return "medium";
  return "small";
};

const onCardClick = (share) => {
  if (!share?.ISINID) return;
  store.dispatch("fetchISINChartData", share);
};

const openShareForum = (share) => {
  if (!share?.ISINID) return;
  store.dispatch("openForum", {
    title: shareName(share) || "Akcie",
    ISINID: share.ISINID,
    ISIN: share.ISIN
  });
};

</script>

<style scoped>
.portfolio-tiles-wrap {
  padding-top: 0.4rem;
  padding-bottom: 0.8rem;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}
.text-slate-400 { color: #94A3B8 !important; }
.fw-extrabold { font-weight: 800; }
.border-dark-subtle { border-color: #1E293B !important; }

.share-logo-wrap { overflow: hidden; }
.share-logo-img {
  width: 40px;
  height: 40px;
  object-fit: contain;
  display: block;
}
.share-logo-img-lg {
  width: 44px;
  height: 44px;
}

.cash-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  background: rgba(5, 150, 105, 0.16);
  color: #059669;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: -0.05em;
}

.cash-logo-sm {
  width: 28px;
  height: 28px;
  font-size: 0.48rem;
}

.cash-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.9rem;
  border-radius: 0.65rem;
}

.cash-row-title {
  font-size: 0.9rem;
  color: #0F172A;
  min-width: 0;
  flex: 1 1 auto;
}

.cash-row-amount {
  font-size: 1.05rem;
  color: #0F172A;
  letter-spacing: -0.03em;
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
}

.cash-row-interest {
  font-size: 0.72rem;
  font-weight: 500;
  color: #64748B;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
}

.cash-row-dark .cash-row-interest {
  color: #94A3B8;
}

.cash-row-rate {
  font-weight: 700;
}

.cash-row-rate.is-good {
  color: #059669;
  font-weight: 800;
}

.cash-row-dark .cash-row-rate.is-good {
  color: #34D399;
}

.cash-row-interest .is-warn {
  color: #EA580C;
}

.cash-row-interest .is-warn .cash-row-rate {
  color: #EA580C;
  font-weight: 800;
}

.cash-row-dark .cash-row-interest .is-warn,
.cash-row-dark .cash-row-interest .is-warn .cash-row-rate {
  color: #FF8A4C;
}

.cash-row-interest .is-muted,
.cash-row-interest .is-muted .cash-row-rate {
  color: #94A3B8;
  font-weight: 500;
}

.cash-row-dark .cash-row-interest .is-muted,
.cash-row-dark .cash-row-interest .is-muted .cash-row-rate {
  color: #475569;
}

.cash-row-light {
  background: #ffffff;
  border: 1px solid #E2E8F0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.cash-row-dark {
  background: #1E293B;
  border: 1px solid #334155;
}

.cash-row-dark .cash-row-title,
.cash-row-dark .cash-row-amount {
  color: #E2E8F0;
}

.cash-interest-block {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0.7rem 0.75rem 0.8rem;
  border-radius: 0.75rem;
}

.cash-interest-panel-light {
  background: #F1F5F9;
}

.cash-interest-panel-dark {
  background: #0F172A;
}

.cash-tile-amount-wrap {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  min-height: 0;
}

.cash-tile-amount {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 0.4rem;
  margin: 0;
  text-align: right;
}

.cash-tile-currency {
  font-size: 0.95rem;
  font-weight: 600;
  position: relative;
  top: -0.02em;
}

.cash-interest-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #64748B;
  text-align: center;
  margin-bottom: 0.65rem;
}

.option-card-dark .cash-interest-title {
  color: #94A3B8;
}

.cash-bands {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.15rem;
  width: 100%;
  max-width: 100%;
}

.cash-band {
  padding: 0.65rem 0.75rem;
  border-radius: 0.65rem;
  text-align: center;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.cash-interest-panel-dark .cash-band {
  background: #1E293B;
  border-color: #334155;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.cash-interest-panel-light .cash-band.is-active {
  border-color: #059669;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.18);
}

.cash-interest-panel-dark .cash-band.is-active {
  border-color: #34D399;
  box-shadow: 0 2px 8px rgba(52, 211, 153, 0.18);
}

.cash-band.is-warn .cash-band-rate,
.cash-band.is-high.is-warn .cash-band-rate {
  color: #EA580C !important;
}

.option-card-dark .cash-band.is-warn .cash-band-rate,
.option-card-dark .cash-band.is-high.is-warn .cash-band-rate {
  color: #FF8A4C !important;
}

.cash-band.is-warn .cash-band-rate span,
.cash-band.is-high.is-warn .cash-band-rate span {
  color: #C2410C !important;
}

.option-card-dark .cash-band.is-warn .cash-band-rate span,
.option-card-dark .cash-band.is-high.is-warn .cash-band-rate span {
  color: #FDBA74 !important;
}

.cash-band.is-muted .stat-label,
.cash-band.is-muted .cash-band-rate,
.cash-band.is-muted .cash-band-rate span {
  color: #64748B !important;
}

.option-card-dark .cash-band.is-muted .stat-label,
.option-card-dark .cash-band.is-muted .cash-band-rate,
.option-card-dark .cash-band.is-muted .cash-band-rate span {
  color: #475569 !important;
}

.cash-band-rate {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0F172A;
  letter-spacing: -0.03em;
  margin-top: 0.1rem;
}

.cash-band-rate span {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748B;
}

.option-card-dark .cash-band-rate {
  color: #E2E8F0;
}

.option-card-dark .cash-band-rate span {
  color: #94A3B8;
}

.option-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  border-radius: 0.65rem !important;
}
.option-card.is-clickable:hover {
  transform: translateY(-2px);
}
.option-card-dark {
  background-color: #1E293B !important;
  border: 1px solid #334155 !important;
  color: #E2E8F0;
}

.range-footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  column-gap: 0.45rem;
  margin-top: 0.15rem;
  font-size: 0.75rem;
}
.range-title {
  text-align: center;
  white-space: nowrap;
  font-weight: 600;
}
.range-end {
  min-width: 0;
}
.range-end-max {
  text-align: right;
}
.position-stats {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.55rem 0.7rem;
  align-items: start;
  padding: 0.7rem 0.75rem;
  border-radius: 0.65rem;
}
.stat-col {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.18rem;
}
.stat-actions {
  align-items: center;
  text-align: center;
  justify-self: center;
  align-self: center;
  padding-right: 0;
}
.stat-actions .stat-label {
  width: 100%;
}
.stat-actions-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  margin-top: 0.1rem;
}
.stat-sub {
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.25;
  color: #64748B;
}
.stat-sub-label {
  display: block;
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94A3B8;
}
.stat-sub.text-success .stat-sub-label,
.stat-sub.text-danger .stat-sub-label {
  color: inherit;
  opacity: 0.85;
}
.option-card-dark .stat-sub {
  color: #94A3B8;
}
.position-stats-light {
  background: #F8FAFC;
}
.position-stats-dark {
  background: #0F172A;
}

.smart-lines {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  column-gap: 0.5rem;
  padding: 0.22rem 0.45rem;
  border-radius: 0.5rem;
}

.smart-col {
  min-width: 0;
  display: flex;
}

.smart-col.is-left {
  justify-content: flex-start;
}

.smart-col.is-right {
  justify-content: flex-end;
}

.smart-lines-light {
  background: #F8FAFC;
}

.smart-lines-dark {
  background: #0F172A;
}

.smart-line {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  min-height: 1.1rem;
  line-height: 1.1;
}

.smart-line-badge {
  flex: 0 0 auto;
  font-size: 0.56rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.08rem 0.38rem;
  border-radius: 999px;
}

.smart-line.is-sl .smart-line-badge,
.smart-line.is-tr .smart-line-badge {
  background: rgba(255, 138, 76, 0.16);
  color: #EA580C;
}

.smart-line.is-tp .smart-line-badge {
  background: rgba(16, 185, 129, 0.14);
  color: #059669;
}

.option-card-dark .smart-line.is-sl .smart-line-badge,
.option-card-dark .smart-line.is-tr .smart-line-badge {
  color: #FF8A4C;
}

.option-card-dark .smart-line.is-tp .smart-line-badge {
  color: #34D399;
}

.smart-line-limit {
  font-size: 0.78rem;
  font-weight: 800;
  color: #0F172A;
  letter-spacing: -0.02em;
  white-space: nowrap;
}

.option-card-dark .smart-line-limit {
  color: #E2E8F0;
}

.smart-line-pct {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748B;
}

.option-card-dark .smart-line-pct {
  color: #94A3B8;
}

.smart-line-actions {
  display: inline-flex;
  align-items: center;
  gap: 0;
  margin-left: 0.05rem;
}

.smart-line-btn {
  flex: 0 0 auto;
  width: 0.95rem;
  height: 1.15rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #94A3B8;
  font-size: 0.62rem;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.smart-line-btn:hover {
  color: #EA580C;
}

.smart-line-btn.is-clear:hover {
  color: #DC2626;
}
.stat-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94A3B8;
}
.stat-value {
  font-size: 0.82rem;
  font-weight: 800;
  color: #0F172A;
}
.stat-value.stat-value-qty {
  font-size: 1.41rem;
  line-height: 1.05;
  letter-spacing: -0.03em;
  font-weight: 800;
}
.stat-value.stat-value-qty .stat-unit {
  font-size: 0.78rem;
  font-weight: 700;
  color: #94A3B8;
  letter-spacing: 0;
  margin-left: 3px;
}
.stat-col-open {
  gap: 0;
  justify-self: end;
  align-items: flex-end;
  text-align: right;
}
.stat-col-open .stat-label {
  line-height: 1.1;
}
.stat-col-open .stat-label + .stat-value {
  margin-top: 0.08rem;
  line-height: 1.1;
}
.stat-sub-change {
  margin-top: 0.16rem;
  font-size: 0.72rem;
}
.stat-sub-change .stat-sub-label {
  font-size: 0.65rem;
  margin-bottom: 0.12rem;
}
.option-card-dark .stat-value {
  color: #E2E8F0;
}

.badge-change-up-dark {
  background-color: rgba(52, 211, 153, 0.16) !important;
  color: #34D399 !important;
}
.badge-change-down-dark {
  background-color: rgba(248, 113, 113, 0.16) !important;
  color: #F87171 !important;
}
.badge-change-flat-dark {
  background-color: #0F172A !important;
  color: #94A3B8 !important;
}

.forum-bubble-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
}
.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}
.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.side-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.12rem 0.4rem;
  border-radius: 999px;
  line-height: 1.2;
}

.side-badge.is-long {
  background: rgba(16, 185, 129, 0.14);
  color: #059669;
}

.side-badge.is-short {
  background: rgba(255, 138, 76, 0.16);
  color: #EA580C;
}

.option-card-dark .side-badge.is-long {
  background: rgba(52, 211, 153, 0.16);
  color: #34D399;
}

.option-card-dark .side-badge.is-short {
  background: rgba(255, 138, 76, 0.2);
  color: #FF8A4C;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}

.trade-action-btn {
  width: 20px;
  height: 20px;
  border: 0;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.95rem;
  line-height: 1;
}

.trade-action-btn-dark {
  color: #94A3B8;
}

.trade-action-close:hover:not(:disabled):not(.is-disabled),
.trade-action-btn-dark.trade-action-close:hover:not(:disabled):not(.is-disabled) {
  color: #EF4444;
}

.trade-action-shield:hover:not(:disabled):not(.is-disabled),
.trade-action-btn-dark.trade-action-shield:hover:not(:disabled):not(.is-disabled) {
  color: #FF8A4C;
}

.trade-action-long:hover:not(:disabled):not(.is-disabled),
.trade-action-btn-dark.trade-action-long:hover:not(:disabled):not(.is-disabled) {
  color: #10B981;
}

.trade-action-short:hover:not(:disabled):not(.is-disabled),
.trade-action-btn-dark.trade-action-short:hover:not(:disabled):not(.is-disabled) {
  color: #FF8A4C;
}

.trade-action-btn:disabled,
.trade-action-btn.is-disabled,
.trade-action-btn-dark:disabled,
.trade-action-btn-dark.is-disabled {
  color: #94A3B8 !important;
  opacity: 0.32 !important;
  cursor: not-allowed;
  filter: grayscale(1);
  pointer-events: none;
}

.trade-action-btn:disabled :deep(svg),
.trade-action-btn.is-disabled :deep(svg),
.trade-action-btn-dark:disabled :deep(svg),
.trade-action-btn-dark.is-disabled :deep(svg) {
  color: #94A3B8 !important;
  fill: currentColor !important;
  opacity: 0.45;
}
</style>

<style>
.option-card .trade-action-btn:disabled,
.option-card .trade-action-btn.is-disabled {
  color: #94A3B8 !important;
  opacity: 0.32 !important;
  filter: grayscale(1);
  cursor: not-allowed;
  pointer-events: none;
}
.option-card .trade-action-btn:disabled svg,
.option-card .trade-action-btn.is-disabled svg {
  color: #94A3B8 !important;
  fill: currentColor !important;
}

.option-card.option-card-dark {
  background-color: #1E293B !important;
  background-image: none !important;
  border: 1px solid #334155 !important;
  border-radius: 0.65rem !important;
}

.cash-band.is-warn {
  border-color: #FF8A4C !important;
}

.cash-band.is-warn .cash-band-rate {
  color: #EA580C !important;
}

.cash-band.is-warn .cash-band-rate span {
  color: #C2410C !important;
}

.option-card-dark .cash-band.is-warn .cash-band-rate {
  color: #FF8A4C !important;
}

.option-card-dark .cash-band.is-warn .cash-band-rate span {
  color: #FDBA74 !important;
}
</style>
